import { animate, AnimationBuilder, AnimationFactory, AnimationPlayer, style } from '@angular/animations';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppointmentStatus, Department, IAppointment, IEmployee, ITeam } from '@signal/asp-data-commons';
import { IAppointmentByTime, IScheduleAvailability } from '@signal/asp-data-commons/lib/interfaces/availability';
import { DateTime } from 'luxon';
import { combineLatest, Observable, of } from 'rxjs';
import * as fromTeamsSelectors from '../../../../store/team/team.selectors';
import * as fromTeamsActions from '../../../../store/team/team.actions';
import * as fromActions from '../../../../store/slot/slot.actions';
import * as fromSelector from '../../../../store/slot/slot.selectors';
import * as fromSalesAvailabilityActions from '../../../../store/sales-availability/sales-availability.actions';
import * as fromSalesAvailabilitySelector from '../../../../store/sales-availability/sales-availability.selectors';
import { TeamState } from 'src/app/modules/store/team/team.reducer';
import * as fromScheduleDashboardSelector from '../../../../store/schedule-dashboard/schedule-dashboard.selectors';
import { ScheduleDashboardState } from 'src/app/modules/store/schedule-dashboard/schedule-dashboard.reducer';
import { SlotState } from 'src/app/modules/store/slot/slot.reducer';
import { PerDaySlotsForCustomData, ShopMaxPerDay } from '../../../models/slot-management.model';
import { SalesAvailabilityState } from 'src/app/modules/store/sales-availability/sales-availability.reducer';
import {TranslateService} from '@ngx-translate/core';
import { selectAllVehiclesFromInventory, selectVehiclesLoaded } from 'src/app/modules/store/vehicle/vehicle.selectors';
import { VehiclesState } from 'src/app/modules/store/vehicle/vehicle.reducer';
import { SubscriptionList } from '../../../models/asp.types';
import * as util from '../../../../shared/services/util.service';

@Component({
  selector: 'app-appointment-by-day',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './appointment-by-day.component.html',
  styleUrls: ['./appointment-by-day.component.scss']
})
export class AppointmentByDayComponent implements OnInit, OnChanges, OnDestroy {

  timeArray: string[] = ['12 AM', '1 AM', '2 AM', '3 AM', '4 AM', '5 AM', '6 AM', '7 AM', '8 AM', '9 AM', '10 AM', '11 AM', '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM', '6 PM', '7 PM', '8 PM', '9 PM', '10 PM', '11 PM'];
  currentSlide = 0;
  private player: AnimationPlayer;
  teams$: Observable<ITeam[]>;
  isApptLoading$: Observable<boolean>;
  teamList: ITeam[] = [];
  isOtherFilter: boolean = false;
  appointmentByTime : IAppointmentByTime[] = [];
  appointmentsForDay: IAppointment[] = [];
  teamMaxTotal$: Observable<PerDaySlotsForCustomData[]>;
  salesShopMaxSlots$: Observable<ShopMaxPerDay[]>;
  advisorList: IEmployee[] = [];
  teamMax: PerDaySlotsForCustomData[] = [];
  salesShopMax : ShopMaxPerDay[] = [];
  private subscr:SubscriptionList = {};
  @Input() filterValue: any;
  @Input() dateSlected: IScheduleAvailability;
  @Input() department: string;
  @Input() dealerStartTime: string;
  appointmentsForDay$: Observable<IAppointment[]>;
  @Output() advisorListChange: EventEmitter<IEmployee[]> = new EventEmitter<IEmployee[]>();
  @ViewChild('timeDiv', {static: false}) timeDiv: ElementRef;
  @ViewChild('timeCarousel') timeCarousel: ElementRef;
  isVehiclesLoading$: Observable<boolean>;
  allInventoryVehicles : any[];

  constructor(private readonly builder: AnimationBuilder,   private readonly slotState: Store<SlotState>,  private readonly salesSlotState: Store<SalesAvailabilityState>, private scheduleDashboardState: Store<ScheduleDashboardState>, private readonly teamsState: Store<TeamState>,
    private readonly translate: TranslateService,
    private readonly vehicleState: Store<VehiclesState>) { 
  }

  ngOnInit(): void {
    this.setCurrentSlideFromDealerStartTime();
    this.slideAnimation();
    this.setupStore();
   this.initializeSubs();
  }

  
  ngOnChanges(changes: SimpleChanges): void {
    if (!this.timeCarousel) {
      return;
    }

    if(changes.hasOwnProperty('dealerStartTime')){
      this.setCurrentSlideFromDealerStartTime();
    }
    
    if(changes.hasOwnProperty('dateSlected') || changes.hasOwnProperty('filterValue')){
      if((changes.dateSlected?.currentValue !== changes.dateSlected?.previousValue) || 
      (changes.filterValue?.currentValue !== changes.filterValue?.previousValue)){
        this.setCurrentSlideFromDealerStartTime();
    }
    }
    setTimeout(() => {
      this.slideAnimation();
    });
  }

  teamMaxSlot(teamId?: string) {
    const weekdays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    let capacity = 0;
    if (this.dateSlected && this.teamMax) {
    capacity = this.teamMax?.find(x=>x.dayOfWeek.toUpperCase() === weekdays[this.dateSlected?.date?.getDay()])?.capacities.find(y => y.id === teamId)?.capacity || 0; 
    }
    return capacity;
  }

  salesShopMaxSlot(){
    const weekdays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    let capacity = 0;
    if(this.dateSlected && this.salesShopMax){
      capacity = this.salesShopMax?.find(x=>x.dayOfWeek.toUpperCase() === weekdays[this.dateSlected?.date?.getDay()])?.slotCapacity;
    }
    return capacity;
  }

  isApptAvailable(team: ITeam | null, type: string, employee: IEmployee | null) {
    if (type === 'team') {
      return this.isTeamApptAvailable(team);
    }

    return this.isAdvisorApptAvailable(employee);
  }

  private isTeamApptAvailable(team: ITeam | null): boolean {
    if (!team) {
      return false;
    }

    if (team.id === 'OTHER') {
      return this.hasOtherTeamAppointments() && this.matchesTeamFilter(team.id);
    }

    return this.teamHasAppointments(team) && this.matchesTeamFilter(team.id);
  }

  private teamHasAppointments(team: ITeam): boolean {
    const employeeIds = team.employeeIds ?? [];
    return this.appointmentsForDay.some(appt => employeeIds.includes(appt.advisorId));
  }

  private hasOtherTeamAppointments(): boolean {
    return this.appointmentsForDay.some(appt =>
      this.teamList.findIndex(team => team.employeeIds?.includes(appt.advisorId)) === -1
    );
  }

  private matchesTeamFilter(teamId: string): boolean {
    const selectedTeamId = this.filterValue?.teamId;
    return !selectedTeamId || selectedTeamId === 'ALL' || selectedTeamId === teamId;
  }

  private isAdvisorApptAvailable(employee: IEmployee | null): boolean {
    if (!employee?.id) {
      return false;
    }

    return this.appointmentsForDay.some(appt => appt.advisorId === employee.id);
  }

  apptCountForTeam(team:ITeam){
    if(this.appointmentsForDay.filter(appt => team.employeeIds?.includes(appt.advisorId))?.length > 0 && team.id !== 'OTHER'){
      return this.appointmentsForDay.filter(appt => appt.appointmentStatus !== AppointmentStatus.CANCELED && team.employeeIds?.includes(appt.advisorId))?.length ;
    } else if(team.id === 'OTHER' && this.appointmentsForDay.filter(x => this.teamList.findIndex(y=>y.employeeIds?.includes(x.advisorId)) === -1 )?.length > 0){
      return this.appointmentsForDay.filter(x => x.appointmentStatus !== AppointmentStatus.CANCELED && this.teamList.findIndex(y=> y.employeeIds?.includes(x.advisorId)) === -1)?.length ;
    }else {
      return 0;
    }
  }

  apptCountForAdvisor(advisor: IEmployee){
    return this.appointmentsForDay.filter(x=>x.advisorId === advisor.id)?.length || 0;
  }

  getMaxCount(team:ITeam){
    let appointmentList : IAppointment[] = [];
    if(this.appointmentsForDay.filter(appt => team.employeeIds?.includes(appt.advisorId))?.length > 0 && team.id !== 'OTHER'){
        appointmentList = this.appointmentsForDay.filter(appt => team.employeeIds?.includes(appt.advisorId)) ;
      } else if(team.id === 'OTHER' && this.appointmentsForDay.filter(x => this.teamList.findIndex(y=>y.employeeIds?.includes(x.advisorId)) === -1 )?.length > 0){
        appointmentList = this.appointmentsForDay.filter(x => this.teamList.findIndex(y=>y.employeeIds?.includes(x.advisorId)) === -1) ;
      }
    return Math.max(0, ...this.timeArray.map(hour => this.getAppointmentsForTimeSlot(appointmentList, hour).length));
  }

  getMaxCountSales(advisor: IEmployee){
    let appointmentList : IAppointment[] = [];
    if(this.appointmentsForDay.filter(appt => appt.advisorId === advisor.id)?.length > 0){
      appointmentList = this.appointmentsForDay.filter(appt => appt.advisorId === advisor.id);
    }
    return Math.max(0, ...this.timeArray.map(hour => this.getAppointmentsForTimeSlot(appointmentList, hour).length));
  }

  setupStore(){
    this.teamsState.dispatch(new fromTeamsActions.LoadTeams());
    this.slotState.dispatch(new fromActions.LoadTeamMaxTotal());
    this.salesSlotState.dispatch(new fromSalesAvailabilityActions.LoadShopMaxTotal());
    this.teams$ = this.teamsState.select(fromTeamsSelectors.selectTeams);
    this.appointmentsForDay$ = this.scheduleDashboardState.select(fromScheduleDashboardSelector.selectScheduleDashboardAppointmentsForDay,{department: this.department});
    this.isApptLoading$ = this.scheduleDashboardState.select(fromScheduleDashboardSelector.selectScheduleDashboardLoading);
    this.teamMaxTotal$ = this.slotState.select(fromSelector.selectTeamMaxTotal);
    this.salesShopMaxSlots$ = this.salesSlotState.select(fromSalesAvailabilitySelector.selectShopMaxTotal);
    if (this.department === Department.SALES) {
      this.isVehiclesLoading$ = this.vehicleState.select(selectVehiclesLoaded);
      this.subscr.vehicleSub = combineLatest([
        this.vehicleState.select(selectVehiclesLoaded),
        this.vehicleState.select(selectAllVehiclesFromInventory)
      ]).subscribe(([loaded, allVehicles]) => {
        if (loaded && allVehicles) {
          this.allInventoryVehicles = allVehicles?.length ? allVehicles : [];
        }
      });
    } else if (this.department === Department.PARTS) {
      this.isVehiclesLoading$ = of(true);
      this.allInventoryVehicles = [];
    } else {
      this.isVehiclesLoading$ = new Observable((observer) => {
        observer.next(true);
        observer.complete();
      });
    }
  }

  getAdvisorDisplayName(advisor: IEmployee) {
    return advisor?.displayName || advisor?.firstName || this.translate.instant('serviceSchedule.notApplicable');
  }

  private buildAdvisorList(appointmentsForDay: IAppointment[]): IEmployee[] {
    const advisorMap = new Map<string, IEmployee>();

    (appointmentsForDay || []).forEach(appointment => {
      const advisorId = appointment?.advisorId;
      if (!advisorId || advisorMap.has(advisorId)) {
        return;
      }

      const displayName = appointment?.advisor?.displayName
        || (advisorId === '-1' ? this.translate.instant('testDrive.anyConsultant') : this.translate.instant('serviceSchedule.notApplicable'));

      advisorMap.set(advisorId, {
        ...(appointment?.advisor || {}),
        id: advisorId,
        displayName
      } as IEmployee);
    });
    return Array.from(advisorMap.values());
  }

  initializeSubs(){
    this.subscr.isApptLoading = this.isApptLoading$.subscribe(_isLoading => {
    });
    this.subscr.isVehiclesLoading = this.isVehiclesLoading$.subscribe(_isLoading => {
    });
    this.subscr.appointmentsForDay = this.appointmentsForDay$.subscribe(appointmentsForDay => { 
      this.appointmentsForDay = appointmentsForDay? appointmentsForDay : [];
      if (this.department === Department.PARTS) {
        this.setCurrentSlideFromDealerStartTime();
      }
      setTimeout(() => {
        this.slideAnimation();
      });
      this.advisorList = this.buildAdvisorList(this.appointmentsForDay);
      this.advisorListChange.emit(this.advisorList);
      });
    this.subscr.teamList = this.teams$.subscribe(teamList => {
      this.teamList = [{department: Department.SERVICE, name: this.translate.instant('serviceSchedule.others'), createdOn: '', dealerCode: '', id:'OTHER', employeeIds: []}];
      this.teamList = [...teamList, ...this.teamList];
    });
    this.subscr.teamMax = this.teamMaxTotal$.subscribe(data => {
      this.teamMax = data;
    });
    this.subscr.shopMax = this.salesShopMaxSlots$.subscribe(data => {
      this.salesShopMax = data;
    })
  }

  onPrev() {
    if (this.currentSlide === 0 || !this.timeCarousel) {
      return;
    }

    this.currentSlide = ((this.currentSlide - 1) + this.timeArray.length) % this.timeArray.length;
    this.slideAnimation();
    
  }

  onNext() {
    if (this.currentSlide + 1 === this.timeArray.length || !this.timeCarousel) {
      return;
    }
    this.currentSlide = (this.currentSlide + 1) % this.timeArray.length;
    this.slideAnimation();
   
  }

  getApptForTeam(appointment: IAppointment[], team: ITeam){
    if(appointment.length > 0){
      const appointmentList = team.id !== 'OTHER' ? appointment.filter(x => team.employeeIds?.includes(x.advisorId)) : appointment.filter(x => this.teamList.findIndex(y=>y.employeeIds?.includes(x.advisorId)) === -1);
    return appointmentList;
    }
    return appointment;
  }

  getApptForAdvisor(appointment: IAppointment[], advisor: IEmployee){
    if(appointment.length > 0){
      return appointment.filter(x => x.advisorId === advisor?.id);
      }
      return appointment;
  }

  private slideAnimation() {
    if (!this.timeDiv || !this.timeCarousel) {
      return;
    }
    const offset = +getComputedStyle(this.timeDiv?.nativeElement)
      ?.width?.replace('px', '') || 95;
    const marginRight = +getComputedStyle(this.timeDiv?.nativeElement)
      .marginRight?.replace('px', '') || 0;
    const offsetCalc = this.currentSlide * (offset + marginRight) * -1;
    const offsetPx = offsetCalc === 0 ? '0px' : offsetCalc + 'px';
    const myAnimation: AnimationFactory = this.builder.build([
      animate('250ms ease-in', style({transform: `translateX(${offsetPx})`}))
    ]);

    this.player?.destroy();
    this.player = myAnimation.create(this.timeCarousel?.nativeElement);
    try {
      this.player.play();
    } catch (e) {
      console.warn('Error when animating: ', e.message);
    }
  }

  private setCurrentSlideFromDealerStartTime() {
    const dealerStart = this.dealerStartTime?.trim();
    const slideIndex = this.timeArray.findIndex(x => DateTime.fromFormat(x, 'h a').toFormat('h:ss a') === dealerStart);
    this.currentSlide = slideIndex >= 0 ? slideIndex : 0;

    if (this.department === Department.PARTS && slideIndex < 0) {
      this.setCurrentSlideFromAppointments();
    }
  }

  private setCurrentSlideFromAppointments() {
    const appointmentTimes = (this.appointmentsForDay || [])
      .map(appointment => this.parseTimeSlot(appointment?.timeSlotId))
      .filter((time): time is DateTime => !!time)
      .sort((a, b) => a.toMillis() - b.toMillis());

    if (!appointmentTimes.length) {
      return;
    }

    const slideIndex = this.timeArray.findIndex(hour => DateTime.fromFormat(hour, 'h a').hasSame(appointmentTimes[0], 'hour'));
    if (slideIndex >= 0) {
      this.currentSlide = slideIndex;
    }
  }

  getAppointmentsForTime(){
    this.appointmentByTime = this.timeArray.map(timeId => ({
      appointments: this.getAppointmentsForTimeSlot(this.appointmentsForDay || [], timeId),
      timeSlotId: timeId
    }));
    return this.appointmentByTime;
  }

  private getAppointmentsForTimeSlot(appointments: IAppointment[], hourLabel: string): IAppointment[] {
    const hourTime = DateTime.fromFormat(hourLabel, 'h a');

    if (!hourTime.isValid) {
      return [];
    }

    return appointments.filter(appointment => this.parseTimeSlot(appointment.timeSlotId)?.hasSame(hourTime, 'hour'));
  }

  private parseTimeSlot(timeSlot: string | undefined): DateTime | null {
    if (!timeSlot) {
      return null;
    }

    const supportedFormats = ['hh:mm a', 'h:mm a'];

    for (const format of supportedFormats) {
      const parsedTime = DateTime.fromFormat(timeSlot, format);
      if (parsedTime.isValid) {
        return parsedTime;
      }
    }

    return null;
  }

ngOnDestroy() {
  util.unsubscribeSubscriptions(this.subscr);
}

}
